import { useEffect, useState, PropsWithChildren, useRef } from 'react'
import { useFormikContext } from 'formik'
import { useWizardSchema } from '../Wizard/WizardContext'
import { useAutocomplete } from '../DesktopWizards/useAutocomplete'
import { AutofillContext } from './AutofillContext'
import { PollFieldsetCompletion } from 'components/PollFieldsetCompletion/PollFieldsetCompletion'

// Types
import { MultiStepSchema, StepSchema, WizardType } from '../schemas/Types'
import { FieldSchema } from '../Wizard/Form/Types'
import { FieldsetCompletion } from 'types'

interface AutofillManagerProps {
  /**
   * A list of field names that belong to the step currently being rendered.
   * When provided, the manager will use this list instead of inferring fields
   * from the wizard schema. This is useful for mobile wizards where each step
   * renders its own form in isolation.
   */
  fieldNames?: string[]
  setIsLoading?: (isLoading: boolean) => void
}

export const AutofillManager: React.FC<PropsWithChildren<AutofillManagerProps>> = ({
  fieldNames,
  setIsLoading,
  children,
}) => {
  const formik = useFormikContext<Record<string, unknown>>()
  const schema = useWizardSchema() as MultiStepSchema<WizardType>
  const prevBrandKitIdRef = useRef<unknown>(formik.values.brand_kit_id)

  const isAutofillField = (fs: FieldSchema) => {
    const meta = fs.describe().meta
    return meta.autoFill || meta.generate
  }

  const [loadingFields, setLoadingFields] = useState<Set<string>>(new Set())
  const [completionId, setCompletionId] = useState<number | null>(null)

  const getCurrentStep = (): StepSchema | undefined =>
    schema.steps.find((step) => Object.keys(step.fields).some((f) => Object.keys(formik.values).includes(f)))

  const getTargets = () => {
    if (fieldNames && fieldNames.length > 0) {
      const filtered = fieldNames.filter((name) => {
        const fs = (
          schema.steps.find((s) => Object.prototype.hasOwnProperty.call(s.fields as Record<string, FieldSchema>, name))
            ?.fields as Record<string, FieldSchema> | undefined
        )?.[name]

        return fs ? isAutofillField(fs) : false
      })
      return filtered
    }
    const step = getCurrentStep()
    if (!step) return []
    return Object.entries(step.fields as Record<string, FieldSchema>)
      .filter(([, fs]) => isAutofillField(fs as FieldSchema))
      .map(([name]) => name)
  }

  const { handleGenerate, handleComplete, data } = useAutocomplete({
    schema,
    values: formik.values,
    setFieldValue: formik.setFieldValue,
    setStatus: formik.setStatus,
    initialStatus: formik.initialStatus,
  })

  useEffect(() => {
    if (completionId) return
    const targets = getTargets()
    if (targets.length === 0) return
    const hasEmptyTarget = targets.some((f) => {
      const v = formik.values[f]
      return v === '' || v === undefined || (Array.isArray(v) && v.length === 0)
    })
    if (!hasEmptyTarget) return
    setLoadingFields(new Set(targets))
    setIsLoading?.(true)
    handleGenerate({ fields: targets })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const currentBrandKitId = formik.values.brand_kit_id

    if (currentBrandKitId !== undefined && currentBrandKitId !== prevBrandKitIdRef.current) {
      const fields = ['topic', 'filename'].filter((f) => fieldNames?.includes(f))

      if (fields.length) {
        setIsLoading?.(true)
        setLoadingFields((prev) => new Set([...prev, ...fields]))
        handleGenerate({ fields, newCompletedFields: { brand_kit_id: currentBrandKitId } })
      }

      prevBrandKitIdRef.current = currentBrandKitId
    }
  }, [formik.values.brand_kit_id, fieldNames, handleGenerate, setIsLoading])

  useEffect(() => {
    if (data?.fieldset_completion?.id) setCompletionId(data.fieldset_completion.id)
  }, [data?.fieldset_completion?.id])

  const onDone = async (fc: FieldsetCompletion) => {
    setIsLoading?.(false)
    await handleComplete(fc)
    setLoadingFields(new Set())
    setCompletionId(null)
  }

  return (
    <AutofillContext.Provider
      value={{
        isLoading: (f: string) => loadingFields.has(f),
        completionId,
      }}
    >
      {children}
      {completionId && <PollFieldsetCompletion fieldsetCompletionId={completionId} onComplete={onDone} />}
    </AutofillContext.Provider>
  )
}
